import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
    signal,
    computed,
    effect,
} from "@angular/core";
import { TypeManagerDecorator } from "../main/type.map.service";
import { EventI } from "../pojo/EventI";
import { BpeUtil } from "../modules/bpe/bpe.util";

@TypeManagerDecorator("s25-ng-report-output-file-name")
@Component({
    selector: "s25-ng-report-output-file-name",
    template: `
        @if (init()) {
            <div class="c-margin-left--double ">
                <label class="c-margin-bottom--half">
                    <label [for]="id">Report Output Name:</label>

                    <input
                        class="c-input"
                        type="text"
                        name="outputFileName"
                        [value]="customFilename()"
                        (input)="customFilename.set($any($event.target).value); onChange()"
                    />
                    .{{ extension() }}
                </label>

                <div class="c-margin-bottom--half output-file-section">
                    Full Output Filename: <strong>{{ fullOutputFilename() }}</strong>
                </div>
            </div>
        }
    `,
    styles: `
        .output-file-section {
            word-break: break-all;
            white-space: normal;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class S25ReportOutputFileNameComponent implements OnInit {
    reportFilename = signal<string>("");

    @Input()
    set reportFilenameInput(value: string | null) {
        this.reportFilename.set(value ?? "");
    }

    @Input() eventData?: EventI & {
        event_locator?: string;
        event_id?: number;
        event_name?: string;
        start_date?: string;
    };
    @Input() reportId?: number;
    @Output() modelValueChange = new EventEmitter<any>();

    static count: number = 0;
    id: string;
    init = signal(false);
    extension = signal<string>("pdf");
    customFilename = signal<string>("");
    selectedFormat = signal<string>("");

    // computed signal for full filename
    fullOutputFilename = computed(() => {
        const name = this.sanitizeFileName(this.customFilename());
        const ext = this.extension();
        return ext ? `${name}.${ext}` : name;
    });

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this; // bridge to AngularJS
        this.id = "ngFileType-" + S25ReportOutputFileNameComponent.count++;

        // Effect to update extension when reportFilename changes
        effect(() => {
            const filename = this.reportFilename();
            if (filename && this.init()) {
                const newExt = filename.split(".").pop() || "";
                const currentExt = this.extension();

                if (newExt && newExt !== currentExt) {
                    this.extension.set(newExt);
                    // Update customFilename to remove the extension
                    const nameWithoutExt = this.removeExtension(filename);
                    if (nameWithoutExt) {
                        this.customFilename.set(this.sanitizeFileName(nameWithoutExt));
                    }
                }
            }
        });

        effect(() => {
            if (!this.init()) return;
            this.customFilename();
            this.extension();
            this.onChange();
        });
    }

    async ngOnInit() {
        // Set initial extension
        this.extension.set(this.reportFilename()?.split(".").pop() || "");
        const vars = await BpeUtil.getVariables(this.eventData, null, this.eventData);
        const code = BpeUtil.interpolateString(vars, this.reportFilename());
        const sanitized = this.sanitizeFileName(this.removeExtension(code));
        this.customFilename.set(sanitized);
        this.init.set(true);
    }

    onChange() {
        const data = {
            outputFileName: this.fullOutputFilename(),
            reportId: this.reportId,
        };
        this.modelValueChange.emit(data);
    }

    sanitizeFileName(name: string): string {
        return name.replace(/[\/\\?%*:|"<> ]/g, "_");
    }

    removeExtension(name?: string): string {
        if (!name) return "";
        const i = name.lastIndexOf(".");
        return i === -1 ? name : name.substring(0, i);
    }
}
